import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, errMsg, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/common";
import { CONTACT_EMAIL } from "@/pages/Legal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const SITE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const deleteAccount = async (reason) => {
  try {
    await api.delete("/users/me");
    return "deleted";
  } catch (e) {
    if (![404, 405].includes(e?.response?.status)) throw e;
  }
  const token = localStorage.getItem(TOKEN_KEY);
  await axios.post(`${SITE_API}/account/deletion-request`, { reason: reason || null }, { headers: { Authorization: `Bearer ${token}` } });
  return "requested";
};

export const DeleteAccountPage = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await deleteAccount(reason);
      // Leave the guarded route before logout so RequireAuth doesn't redirect to /login
      nav(`/account/deleted?status=${result}`, { replace: true });
      await logout();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  return (
    <Page className="max-w-xl">
      <h1 data-testid="delete-account-title" className="font-display text-4xl sm:text-5xl font-black tracking-tight">Delete account</h1>
      <div className="mt-8 flex gap-3 p-5 rounded-2xl bg-red-50 border border-red-200 text-sm text-red-900">
        <AlertTriangle className="h-5 w-5 shrink-0" />
        <div>
          <p className="font-semibold">Your account will be permanently deleted from the website and the app.</p>
          <p className="mt-1">Your profile, tickets, follows, reviews, photos and notifications will be removed, and tickets for upcoming events will no longer be valid. If you host upcoming events, they'll be cancelled and your guests refunded. This can't be undone.</p>
        </div>
      </div>
      <form onSubmit={submit} className="mt-8 space-y-5 p-6 rounded-3xl bg-white border border-stone-200">
        <p className="text-sm text-stone-600">Signed in as <span className="font-semibold">{user.email}</span></p>
        <div className="space-y-1.5">
          <Label>Why are you leaving? (optional)</Label>
          <Textarea data-testid="delete-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </div>
        <div className="space-y-1.5">
          <Label>Type DELETE to confirm</Label>
          <Input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="font-mono" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="delete-account-submit-btn" disabled={confirm !== "DELETE" || busy}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-semibold transition-colors">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete my account
          </button>
          <Link to="/profile" data-testid="delete-account-cancel" className="h-12 px-6 inline-flex items-center rounded-full border border-stone-300 font-semibold">Cancel</Link>
        </div>
      </form>
      <p className="mt-6 text-sm text-stone-500">Creator Series is operated by Yappr LLC. Questions? Email <a href={`mailto:${CONTACT_EMAIL}`} data-testid="delete-support-email" className="font-semibold text-[#C2410C]">{CONTACT_EMAIL}</a>.</p>
    </Page>
  );
};

export const AccountDeletedPage = () => {
  const requested = new URLSearchParams(window.location.search).get("status") === "requested";
  return (
    <Page className="max-w-xl text-center">
      <h1 data-testid="account-deleted-title" className="font-display text-4xl font-black">{requested ? "Deletion requested" : "Account deleted"}</h1>
      <p data-testid="account-deleted-message" className="mt-4 text-stone-600">
        {requested ? "We've got your request and will permanently delete your account and data within 30 days. You've been logged out." : "Your account and data have been deleted. Sorry to see you go."}
      </p>
      <Link to="/" className="mt-8 inline-block px-6 py-3 rounded-full bg-stone-900 text-white font-semibold text-sm">Back home</Link>
    </Page>
  );
};
